import React, { useState, useEffect } from 'react';
import {
  CreditCardIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  UsersIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { apiClient } from '../../services/api';

const SubscriptionManagement = () => {
  const [subscriptionStats, setSubscriptionStats] = useState(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Load both subscription stats and plans
      const [statsResponse, plansResponse] = await Promise.all([
        apiClient.get('/admin/subscriptions/stats'),
        apiClient.get('/admin/subscription-plans')
      ]);

      setSubscriptionStats(statsResponse.data.data);
      setSubscriptionPlans(plansResponse.data.plans || []);
      console.log('🔍 SUBSCRIPTION: Loaded plans:', plansResponse.data.plans?.map(p => `${p.code}: PKR ${p.price}`));
    } catch (error) {
      console.error('Failed to load subscription data:', error);
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color, description }) => (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Icon className={`h-6 w-6 ${color}`} />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
              <dd className="text-lg font-medium text-gray-900">{value}</dd>
              {description && (
                <dd className="text-xs text-gray-400 mt-1">{description}</dd>
              )}
            </dl>
          </div>
        </div>
      </div>
    </div>
  );

  // Helper function to get plan color based on code
  const getPlanColor = (code) => {
    const colorMap = {
      'trial': 'gray',
      'basic': 'blue',
      'starter': 'green',
      'premium': 'purple',
      'enterprise': 'yellow'
    };
    return colorMap[code] || 'gray';
  };

  const getColorClasses = (color) => {
    const colors = {
      gray: 'border-gray-200 bg-gray-50 text-gray-600',
      blue: 'border-blue-200 bg-blue-50 text-blue-600',
      green: 'border-green-200 bg-green-50 text-green-600',
      purple: 'border-purple-200 bg-purple-50 text-purple-600',
      yellow: 'border-yellow-200 bg-yellow-50 text-yellow-600'
    };
    return colors[color] || colors.gray;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Subscription Management</h2>
          <p className="text-sm text-gray-600">Manage subscription plans and billing</p>
        </div>
      </div>

      {/* Statistics */}
      {subscriptionStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Total Users"
            value={subscriptionStats.total_users}
            icon={UsersIcon}
            color="text-blue-500"
          />
          <StatCard
            title="Licensed Users"
            value={subscriptionStats.licensed_users}
            icon={CreditCardIcon}
            color="text-green-500"
          />
          <StatCard
            title="Active Licenses"
            value={subscriptionStats.active_licenses}
            icon={CheckCircleIcon}
            color="text-green-500"
          />
          <StatCard
            title="Expired Licenses"
            value={subscriptionStats.expired_licenses}
            icon={ExclamationTriangleIcon}
            color="text-red-500"
          />
        </div>
      )}

      {/* License Type Breakdown */}
      {subscriptionStats && subscriptionPlans.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">License Distribution</h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {subscriptionPlans.map((plan) => {
              const licenseCount = subscriptionStats[`${plan.code}_licenses`] || 0;
              const color = getPlanColor(plan.code);
              const colorClasses = {
                gray: 'bg-gray-50 text-gray-600',
                blue: 'bg-blue-50 text-blue-600',
                green: 'bg-green-50 text-green-600',
                purple: 'bg-purple-50 text-purple-600',
                yellow: 'bg-yellow-50 text-yellow-600'
              };

              return (
                <div key={plan.code} className={`text-center p-4 rounded-lg ${colorClasses[color] || colorClasses.gray}`}>
                  <div className={`text-2xl font-bold ${colorClasses[color]?.replace('bg-', 'text-').replace('-50', '-600') || 'text-gray-600'}`}>
                    {licenseCount}
                  </div>
                  <div className="text-sm text-gray-500 capitalize">{plan.name}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    {plan.price === 0 ? 'Free' : `PKR ${plan.price}/mo`}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Subscription Plans */}
      {subscriptionPlans.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-6">Available Subscription Plans</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {subscriptionPlans.map((plan) => {
              const color = getPlanColor(plan.code);
              // Handle features - could be array (from DB) or string (legacy)
              const features = Array.isArray(plan.features)
                ? plan.features
                : (plan.features ? plan.features.split(',').map(f => f.trim()) : []);

              return (
                <div
                  key={plan.code}
                  className={`border-2 rounded-lg p-6 ${getColorClasses(color)} ${plan.is_recommended ? 'ring-2 ring-blue-500' : ''}`}
                >
                  <div className="text-center">
                    <h4 className="text-lg font-semibold text-gray-900">{plan.name}</h4>
                    <div className="mt-2">
                      <span className="text-3xl font-bold text-gray-900">
                        PKR {plan.price}
                      </span>
                      {plan.price > 0 && (
                        <span className="text-sm text-gray-500">/month</span>
                      )}
                    </div>
                    <div className="mt-2 text-sm text-gray-600">
                      {plan.max_invoices_per_month === -1 ? 'Unlimited' : plan.max_invoices_per_month} invoices/month
                    </div>
                    {plan.description && (
                      <div className="text-xs text-gray-500 mt-1">
                        {plan.description}
                      </div>
                    )}
                  </div>

                  {features.length > 0 && (
                    <ul className="mt-6 space-y-2">
                      {features.map((feature, index) => (
                        <li key={index} className="flex items-center text-sm text-gray-600">
                          <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-6 text-center">
                    {plan.is_recommended && (
                      <div className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 mb-2">
                        ⭐ Recommended
                      </div>
                    )}
                    <div className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getColorClasses(color)}`}>
                      {plan.code === 'trial' ? 'Free Trial' :
                       plan.code === 'basic' ? 'Most Popular' :
                       plan.code === 'starter' ? 'Great Start' :
                       plan.code === 'premium' ? 'Best Value' :
                       'Enterprise'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Revenue Projection */}
      {subscriptionStats && subscriptionPlans.length > 0 && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Revenue Overview</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <CurrencyDollarIcon className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-green-600">
                PKR {subscriptionPlans.reduce((total, plan) => {
                  const licenseCount = subscriptionStats[`${plan.code}_licenses`] || 0;
                  return total + (licenseCount * plan.price);
                }, 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-500">Monthly Recurring Revenue</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <DocumentTextIcon className="h-8 w-8 text-blue-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-blue-600">
                {subscriptionPlans.reduce((total, plan) => {
                  const licenseCount = subscriptionStats[`${plan.code}_licenses`] || 0;
                  const invoiceLimit = plan.max_invoices_per_month === -1 ? 0 : plan.max_invoices_per_month;
                  return total + (licenseCount * invoiceLimit);
                }, 0).toLocaleString()}
              </div>
              <div className="text-sm text-gray-500">Total Monthly Invoice Capacity</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionManagement;
